import { executeContractRequest, executeContractStatus } from '@/api/contract-client';
import type { createContractClient } from '@/api/contract-client';
import { ApiProtocolError } from '@/api/errors';
import type { ApiRegistry } from '@/api/provider';
import type { ExpandedSerp } from '@/api/providers/types';

import { createBackendWordstat } from './wordstat';
import { createBackendHtml } from './html';

export type ContractClient = ReturnType<typeof createContractClient>;

export interface BackendProviderOptions {
  readonly baseUrl: string;
}

const SNAPSHOT_PATH = '/api/v1/serps/{serp_id}/snapshot' as const;

export function createBackendProvider(
  client: ContractClient,
  options: BackendProviderOptions,
): ApiRegistry {
  return {
    html: createBackendHtml(client),
    capabilities: { serpResults: true },
    wordstat: createBackendWordstat(client),
    auth: {
      login: (input) =>
        executeContractRequest(
          client.POST('/api/v1/auth/login', {
            body: input,
          }),
        ),
      me: () => executeContractRequest(client.GET('/api/v1/auth/me')),
      logout: () => executeContractRequest(client.POST('/api/v1/auth/logout')),
    },
    credentials: {
      list: (params = {}) =>
        executeContractRequest(
          client.GET('/api/v1/credentials', {
            params: { query: params },
          }),
        ),
      create: (input) =>
        executeContractRequest(
          client.POST('/api/v1/credentials', {
            body: input,
          }),
        ),
      remove: (id) =>
        executeContractRequest(
          client.DELETE('/api/v1/credentials/{credential_id}', {
            params: { path: { credential_id: id } },
          }),
        ),
      reveal: (id) =>
        executeContractRequest(
          client.POST('/api/v1/credentials/{credential_id}/reveal', {
            params: { path: { credential_id: id } },
          }),
        ),
    },
    targets: {
      list: (params = {}, options) =>
        executeContractRequest(
          client.GET('/api/v1/targets', {
            params: { query: params },
            signal: options?.signal,
          }),
        ),
      get: (id, options) =>
        executeContractRequest(
          client.GET('/api/v1/targets/{target_id}', {
            params: { path: { target_id: id } },
            signal: options?.signal,
          }),
        ),
      create: (input) => executeContractRequest(client.POST('/api/v1/targets', { body: input })),
      update: (id, input) =>
        executeContractRequest(
          client.PATCH('/api/v1/targets/{target_id}', {
            params: { path: { target_id: id } },
            body: input,
          }),
        ),
      remove: (id) =>
        executeContractRequest(
          client.DELETE('/api/v1/targets/{target_id}', {
            params: { path: { target_id: id } },
          }),
        ),
    },
    baskets: {
      list: (params = {}) =>
        executeContractRequest(
          client.GET('/api/v1/baskets', {
            params: { query: params },
          }),
        ),
      get: (id) =>
        executeContractRequest(
          client.GET('/api/v1/baskets/{basket_id}', {
            params: { path: { basket_id: id } },
          }),
        ),
      create: (input) =>
        executeContractRequest(
          client.POST('/api/v1/baskets', {
            body: input,
          }),
        ),
      update: (id, input) =>
        executeContractRequest(
          client.PATCH('/api/v1/baskets/{basket_id}', {
            params: { path: { basket_id: id } },
            body: input,
          }),
        ),
      remove: (id) =>
        executeContractRequest(
          client.DELETE('/api/v1/baskets/{basket_id}', {
            params: { path: { basket_id: id } },
          }),
        ),
      listQueries: (basketId, params = {}) =>
        executeContractRequest(
          client.GET('/api/v1/baskets/{basket_id}/queries', {
            params: { path: { basket_id: basketId }, query: params },
          }),
        ),
      createQuery: (basketId, input) =>
        executeContractRequest(
          client.POST('/api/v1/baskets/{basket_id}/queries', {
            params: { path: { basket_id: basketId } },
            body: input,
          }),
        ),
      removeQuery: (basketId, queryId) =>
        executeContractRequest(
          client.DELETE('/api/v1/baskets/{basket_id}/queries/{query_id}', {
            params: { path: { basket_id: basketId, query_id: queryId } },
          }),
        ),
    },
    experiments: {
      list: (params = {}) =>
        executeContractRequest(
          client.GET('/api/v1/experiments', {
            params: { query: params },
          }),
        ),
      get: (id) =>
        executeContractRequest(
          client.GET('/api/v1/experiments/{experiment_id}', {
            params: { path: { experiment_id: id } },
          }),
        ),
      create: (input) =>
        executeContractRequest(
          client.POST('/api/v1/experiments', {
            body: input,
          }),
        ),
      update: (id, input) =>
        executeContractRequest(
          client.PATCH('/api/v1/experiments/{experiment_id}', {
            params: { path: { experiment_id: id } },
            body: input,
          }),
        ),
      remove: (id) =>
        executeContractRequest(
          client.DELETE('/api/v1/experiments/{experiment_id}', {
            params: { path: { experiment_id: id } },
          }),
        ),
      start: (id) =>
        executeContractRequest(
          client.POST('/api/v1/experiments/{experiment_id}/start', {
            params: { path: { experiment_id: id } },
          }),
        ),
    },
    crons: {
      list: (params = {}) =>
        executeContractRequest(
          client.GET('/api/v1/crons', {
            params: { query: params },
          }),
        ),
      get: (id) =>
        executeContractRequest(
          client.GET('/api/v1/crons/{cron_id}', {
            params: { path: { cron_id: id } },
          }),
        ),
      create: (input) =>
        executeContractRequest(
          client.POST('/api/v1/crons', {
            body: input,
          }),
        ),
      update: (id, input) =>
        executeContractRequest(
          client.PATCH('/api/v1/crons/{cron_id}', {
            params: { path: { cron_id: id } },
            body: input,
          }),
        ),
      remove: (id) =>
        executeContractRequest(
          client.DELETE('/api/v1/crons/{cron_id}', {
            params: { path: { cron_id: id } },
          }),
        ),
      start: (id) =>
        executeContractRequest(
          client.POST('/api/v1/crons/{cron_id}/start', {
            params: { path: { cron_id: id } },
          }),
        ),
      history: (id, params = {}) =>
        executeContractRequest(
          client.GET('/api/v1/crons/{cron_id}/experiments', {
            params: { path: { cron_id: id }, query: params },
          }),
        ),
    },
    analytics: {
      listDynamicsQueryMatrix: (body, options) =>
        executeContractRequest(
          client.POST('/api/v1/analytics/dynamics/query-matrix', { body, signal: options?.signal }),
        ),
      getDynamics: (params, requestOptions) =>
        executeContractRequest(
          client.GET('/api/v1/analytics/dynamics', {
            params: { query: params },
            signal: requestOptions?.signal,
          }),
        ).then(xmlOnly),
      listTargetQueryIndex: (params, requestOptions) =>
        executeContractRequest(
          client.GET('/api/v1/analytics/target-query-index', {
            params: { query: params },
            signal: requestOptions?.signal,
          }),
        ).then(xmlOnly),
    },
    comparison: {
      listQueries: (params, requestOptions) =>
        executeContractRequest(
          client.GET('/api/v1/comparison/queries', {
            params: { query: params },
            signal: requestOptions?.signal,
          }),
        ).then(xmlOnly),
      getComparison: (params, requestOptions) =>
        executeContractRequest(
          client.GET('/api/v1/comparison', {
            params: { query: params },
            signal: requestOptions?.signal,
          }),
        ).then(xmlOnly),
    },
    searchResults: {
      listExperimentResultIndex: (experimentId, params = {}, requestOptions) =>
        executeContractRequest(
          client.GET('/api/v1/experiments/{experiment_id}/result-index', {
            params: { path: { experiment_id: experimentId }, query: params },
            signal: requestOptions?.signal,
          }),
        ).then(xmlOnly),
      async getExpandedSerp(serpId, requestOptions) {
        const page = await executeContractRequest(
          client.POST('/api/v1/serps/search', {
            body: {
              serp_ids: [serpId],
              include_components: true,
              limit: 1,
            },
            signal: requestOptions?.signal,
          }),
        );
        const item = page.items[0];
        if (
          page.items.length !== 1 ||
          !item ||
          item.id !== serpId ||
          !('components' in item) ||
          !Array.isArray(item.components) ||
          item.response_format !== 'xml'
        ) {
          throw new ApiProtocolError('API returned an invalid expanded SERP result');
        }
        return item satisfies ExpandedSerp;
      },
      async probeSnapshot(serpId, requestOptions) {
        await executeContractStatus(
          client.HEAD(SNAPSHOT_PATH, {
            params: { path: { serp_id: serpId } },
            headers: {
              Accept: 'text/html, application/xhtml+xml, application/xml, text/xml;q=0.9',
            },
            signal: requestOptions?.signal,
          }),
        );
      },
      getSnapshotText: (serpId, requestOptions) =>
        executeContractRequest(
          client.GET(SNAPSHOT_PATH, {
            params: { path: { serp_id: serpId } },
            headers: {
              Accept: 'application/xml, text/xml;q=0.9, text/plain;q=0.8',
            },
            parseAs: 'text',
            signal: requestOptions?.signal,
          }),
        ),
      snapshotUrl: (serpId) => snapshotUrl(options.baseUrl, serpId),
      releaseSnapshotUrl: () => undefined,
    },
  };
}

function snapshotUrl(baseUrl: string, serpId: string): string {
  const normalizedBaseUrl = `${baseUrl.replace(/\/+$/u, '')}/`;
  return new URL(
    `/api/v1/serps/${encodeURIComponent(serpId)}/snapshot`,
    normalizedBaseUrl,
  ).toString();
}

function xmlOnly<T>(
  value: T,
): Exclude<
  T,
  { response_format?: 'html' } | { metric_schema_version: 'dynamics_page_metrics_v1' }
> {
  if (value && typeof value === 'object') {
    if ('response_format' in value && value.response_format === 'html')
      throw new ApiProtocolError('HTML response requires the page-coordinate viewer');
    if (
      'metric_schema_version' in value &&
      value.metric_schema_version === 'dynamics_page_metrics_v1'
    )
      throw new ApiProtocolError('HTML response requires page metrics');
  }
  return value as Exclude<
    T,
    { response_format?: 'html' } | { metric_schema_version: 'dynamics_page_metrics_v1' }
  >;
}
